import { useEffect, useMemo } from 'react'

import { useQuery } from '@apollo/client/react'

import { ContentItemCard_ContentItem } from '~publish/components/ContentItemCard'
import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import type { RealTimeContentItem } from '~publish/components/ContentItemCard/useContentItemRealTimePusherUpdates'
import { useContentItemRealTimePusherUpdates } from '~publish/components/ContentItemCard/useContentItemRealTimePusherUpdates'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { client } from '~publish/legacy/apollo-client'

// Schema max per page; the hook pages through the whole range (see below).
const CALENDAR_CONTENT_ITEM_DRAFTS_PAGE_SIZE = 100

export const GetCalendarContentItemDrafts = graphql(
  `
    query GetCalendarContentItemDrafts(
      $first: Int!
      $after: String
      $organizationId: OrganizationId!
      $start: DateTime!
      $end: DateTime!
    ) {
      contentItems(
        first: $first
        after: $after
        input: {
          organizationId: $organizationId
          filter: {
            contentStatus: draftContent
            targetDate: { range: { start: $start, end: $end } }
          }
        }
      ) {
        edges {
          node {
            id
            ...ContentItemCard_ContentItem
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `,
  [ContentItemCard_ContentItem],
)

type GetCalendarContentItemDraftsData = ResultOf<
  typeof GetCalendarContentItemDrafts
>
type GetCalendarContentItemDraftsVariables = VariablesOf<
  typeof GetCalendarContentItemDrafts
>

type UseCalendarContentItemDraftsResult = {
  /** Channel-less drafts whose target date falls in the visible range. */
  drafts: ContentItemDraft[]
  loading: boolean
}

/**
 * Channel-less drafts with a target date inside the calendar's visible range,
 * for placing in day/hour cells. `start`/`end` are the ISO calendar-range
 * instants (see useCalendar).
 *
 * Pages through the whole range so no draft is dropped past the first page.
 * Eagerly fetching every page is safe here (unlike the unfiltered drafts list):
 * the `draftContent` + bounded `targetDate` filter keeps the connection to the
 * range's dated drafts, so it exhausts in a page or two.
 */
export function useCalendarContentItemDrafts({
  start,
  end,
  skip = false,
}: {
  start: string
  end: string
  skip?: boolean
}): UseCalendarContentItemDraftsResult {
  const organizationId = useOrganizationId()

  const { data, loading, fetchMore, variables } = useQuery(
    GetCalendarContentItemDrafts,
    {
      variables: {
        first: CALENDAR_CONTENT_ITEM_DRAFTS_PAGE_SIZE,
        after: null,
        organizationId: organizationId || '',
        start,
        end,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      errorPolicy: 'all',
      skip: skip || !organizationId,
    },
  )

  useContentItemRealTimePusherUpdates({
    skip: skip || !organizationId,
    onItemFetched: (item) =>
      upsertCalendarContentItemDraft(variables, item, start, end),
    onDeleted: (id) => removeCalendarContentItemDraft(variables, id),
  })

  const hasNextPage = data?.contentItems.pageInfo.hasNextPage ?? false
  const endCursor = data?.contentItems.pageInfo.endCursor

  useEffect(() => {
    if (skip || !hasNextPage || !endCursor) return
    // Drop a page that resolves after the range changed: merging its edges +
    // cursor into the new range's connection would mis-drive pagination.
    let ignore = false
    void fetchMore({
      variables: { after: endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (ignore || !fetchMoreResult?.contentItems.edges) return prev
        const newEdges = fetchMoreResult.contentItems.edges
        const newIds = new Set(newEdges.map((edge) => edge.node.id))
        const existingEdges = (prev.contentItems.edges ?? []).filter(
          (edge) => !newIds.has(edge.node.id),
        )
        return {
          ...prev,
          contentItems: {
            ...prev.contentItems,
            edges: [...existingEdges, ...newEdges],
            pageInfo: fetchMoreResult.contentItems.pageInfo,
          },
        }
      },
      // A failed page just stops pagination; earlier pages are kept.
    }).catch(() => {})
    return (): void => {
      ignore = true
    }
  }, [skip, hasNextPage, endCursor, fetchMore])

  const drafts = useMemo(() => {
    const nodes = (data?.contentItems.edges ?? []).map((edge) => edge.node)
    return [...readFragment(ContentItemCard_ContentItem, nodes)]
  }, [data?.contentItems.edges])

  return { drafts, loading }
}

/** Whether a target date falls within `[start, end]`, matching the server's range filter (`$gte`/`$lte`, inclusive at both ends). */
function isWithinRange(
  targetDate: string | null,
  start: string,
  end: string,
): boolean {
  if (targetDate === null) return false
  const time = Date.parse(targetDate)
  return time >= Date.parse(start) && time <= Date.parse(end)
}

/**
 * Inserts, updates, or drops a content item in the calendar drafts cache
 * entry for one visible range. This connection always pages through to
 * exhaustion (see the hook's docs above), so the cache reflects every
 * matching item and a plain prepend/removal is safe without a sort-position
 * concern.
 */
function upsertCalendarContentItemDraft(
  variables: GetCalendarContentItemDraftsVariables | undefined,
  item: RealTimeContentItem,
  start: string,
  end: string,
): void {
  const existingData = client.cache.readQuery<GetCalendarContentItemDraftsData>(
    {
      query: GetCalendarContentItemDrafts,
      variables,
    },
  )
  if (!existingData?.contentItems.edges) {
    return
  }

  const remainingEdges = existingData.contentItems.edges.filter(
    (edge) => edge.node.id !== item.id,
  )
  const unmaskedItem = readFragment(ContentItemCard_ContentItem, item)
  const belongsHere =
    isOfType(unmaskedItem.body, 'DraftContent') &&
    isWithinRange(unmaskedItem.targetDate, start, end)

  if (!belongsHere) {
    if (remainingEdges.length === existingData.contentItems.edges.length) {
      return
    }
    client.cache.writeQuery({
      query: GetCalendarContentItemDrafts,
      variables,
      data: {
        ...existingData,
        contentItems: { ...existingData.contentItems, edges: remainingEdges },
      },
    })
    return
  }

  client.cache.writeQuery({
    query: GetCalendarContentItemDrafts,
    variables,
    data: {
      ...existingData,
      contentItems: {
        ...existingData.contentItems,
        edges: [
          { __typename: 'ContentItemEdge' as const, node: item },
          ...remainingEdges,
        ],
      },
    },
  })
}

/**
 * Drops a content item from the calendar drafts cache entry after a delete
 * event.
 */
function removeCalendarContentItemDraft(
  variables: GetCalendarContentItemDraftsVariables | undefined,
  id: string,
): void {
  const existingData = client.cache.readQuery<GetCalendarContentItemDraftsData>(
    {
      query: GetCalendarContentItemDrafts,
      variables,
    },
  )
  if (!existingData?.contentItems.edges) {
    return
  }

  const remainingEdges = existingData.contentItems.edges.filter(
    (edge) => edge.node.id !== id,
  )
  if (remainingEdges.length === existingData.contentItems.edges.length) {
    return
  }

  client.cache.writeQuery({
    query: GetCalendarContentItemDrafts,
    variables,
    data: {
      ...existingData,
      contentItems: { ...existingData.contentItems, edges: remainingEdges },
    },
  })
}
